import React from 'react';
import YesIcon from '../../../../Assets/Prepratory/picsvg_download (17).svg';
import SometimesIcon from '../../../../Assets/Prepratory/picsvg_download (18).svg';
import NoIcon from '../../../../Assets/Prepratory/picsvg_download (19).svg';
import NotSureIcon from '../../../../Assets/Prepratory/picsvg_download (20).svg';

/**
 * A simple emoji face component for the reflection responses
 * Uses img tags and inline styles for PDF compatibility
 */
const EmojiResponse = ({ type, selected }) => {
    let iconSrc;
    let label;
    switch (type) {
        case 'Yes':
            iconSrc = YesIcon;
            label = 'Yes';
            break;
        case 'Sometimes':
        case 'To an extent':
            iconSrc = SometimesIcon;
            label = 'To an extent';
            break;
        case 'No':
            iconSrc = NoIcon;
            label = 'No';
            break;
        case 'Not sure':
            iconSrc = NotSureIcon;
            label = 'Not sure';
            break;
        default:
            return null;
    }

    return (
        <div className="flex flex-col items-center">
            <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '9999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: selected ? '3px solid #F97316' : '2px solid #D1D5DB',
                backgroundColor: selected ? '#FEF3C7' : '#F3F4F6'
            }}>
                <img src={iconSrc} alt={label} style={{ width: '24px', height: '24px', opacity: selected ? 1 : 0.5 }} />
            </div>
            <div className="text-xs">{label}</div>
        </div>
    );
};

/**
 * Main PeerFeedbackPage component
 */
const PeerFeedbackPage = ({ assignment }) => {
    // Safely extract the peer_assessment data
    const safeData = assignment?.peer_assessment || {};
    const experience = safeData.experience || {};
    const learnings = safeData.learnings || {};
    const progressGrid = safeData.progress_grid || {};

    // Get the arrays of statements directly from the API
    const awarenessStatements = progressGrid.awareness || [];
    const sensitivityStatements = progressGrid.sensitivity || [];
    const creativityStatements = progressGrid.creativity || [];

    // Count selected statements for each category
    const awarenessCount = awarenessStatements.length;
    const sensitivityCount = sensitivityStatements.length;
    const creativityCount = creativityStatements.length;

    // Create a grid of empty cells for each category
    const createEmptyGrid = (count) => {
        return Array(6).fill(null).map((_, index) => index < count ? 'selected' : 'empty');
    };

    return (
        <div className="report-page relative font-[poppins] bg-white" style={{
            width: '210mm',
            height: '297mm',
            padding: '15mm 20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.6', // Increased line-height for more vertical space
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative',
            breakInside: 'avoid',
            pageBreakInside: 'avoid',
            '@media print': {
                margin: 0,
                padding: '15mm 20mm',
                boxShadow: 'none',
                width: '210mm',
                height: '297mm'
            }
        }}>
            {/* Orange corner triangles */}
            <div className="absolute top-0 right-0 w-0 h-0"
                style={{
                    borderTop: '80px solid #f1905b',
                    borderLeft: '80px solid transparent'
                }}>
            </div>
            <div className="absolute bottom-0 left-0 w-0 h-0"
                style={{
                    borderBottom: '80px solid #F97316',
                    borderRight: '80px solid transparent'
                }}>
            </div>
            {/*/!* Orange corner decorations *!/*/}
            {/*<div className="absolute top-0 right-0 w-20 h-20 bg-orange-500"*/}
            {/*     style={{clipPath: 'polygon(100% 0, 0 0, 100% 100%)'}}/>*/}

            <div className="w-full border border-black text-sm bg-white">
                {/* Header */}
                <div className="bg-[#f89646] text-center font-bold text-lg p-2 mb-1">
                    PEER FEEDBACK
                </div>

                {/* Name fields */}
                <div className="mb-1 p-2">
                    <div className="flex justify-between">
                        <div>
                            My name is <span className="px-2">{experience.my_name || ""}</span> .
                        </div>
                        <div>
                            My peer's name is <span className="px-2">{experience.peer_name || ""}</span> .
                        </div>
                    </div>
                </div>

                {/* Based on your experience section */}
                <div className="mb-1 p-2">
                    <div className="mb-2">Based on your experience of the activity, please circle the response that is
                        applicable.
                    </div>

                    <table className="w-full border border-orange-100">
                        <tbody>
                            <tr className="border-b border-orange-100">
                                <td className="p-1 border-r border-orange-100" style={{ width: "60%" }}>
                                    My peer was engaged and motivated during the activity.
                                </td>
                                <td className="p-1 border-r border-orange-100 text-center">
                                    <EmojiResponse
                                        type="Yes"
                                        selected={experience.engaged_and_motivated === "Yes"}
                                    />
                                </td>
                                <td className="p-1 border-r border-orange-100 text-center">
                                    <EmojiResponse
                                        type="To an extent"
                                        selected={experience.engaged_and_motivated === "To an extent"}
                                    />
                                </td>
                                <td className="p-1 border-r border-orange-100 text-center">
                                    <EmojiResponse
                                        type="No"
                                        selected={experience.engaged_and_motivated === "No"}
                                    />
                                </td>
                                <td className="p-1 text-center">
                                    <EmojiResponse
                                        type="Not sure"
                                        selected={experience.engaged_and_motivated === "Not sure"}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td className="p-1 border-r border-orange-100">
                                    My peer effectively shared thoughts and ideas during the activity.
                                </td>
                                <td className="p-1 border-r border-orange-100 text-center">
                                    <EmojiResponse
                                        type="Yes"
                                        selected={experience.shared_thoughts === "Yes"}
                                    />
                                </td>
                                <td className="p-1 border-r border-orange-100 text-center">
                                    <EmojiResponse
                                        type="To an extent"
                                        selected={experience.shared_thoughts === "To an extent"}
                                    />
                                </td>
                                <td className="p-1 border-r border-orange-100 text-center">
                                    <EmojiResponse
                                        type="No"
                                        selected={experience.shared_thoughts === "No"}
                                    />
                                </td>
                                <td className="p-1 text-center">
                                    <EmojiResponse
                                        type="Not sure"
                                        selected={experience.shared_thoughts === "Not sure"}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Progress Grid Section */}
                <div className="mb-1 p-2">
                    <div className="bg-[#fabf8f] text-center font-bold p-2 mb-1">
                        My Peer's Progress Grid
                    </div>

                    <div className="mb-2">Based on your peer's engagement with the activity, circle the statements you
                        think
                        are true for your peer.
                    </div>

                    <table className="w-full border border-gray-300">
                        <thead>
                            <tr>
                                <th className="bg-[#ec99b6] text-center p-2 border border-gray-300 font-bold w-1/3">
                                    Awareness
                                </th>
                                <th className="bg-[#faf8b4] text-center p-2 border border-gray-300 font-bold w-1/3">
                                    Sensitivity
                                </th>
                                <th className="bg-[#7dddff] text-center p-2 border border-gray-300 font-bold w-1/3">
                                    Creativity
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Display statements from the API */}
                            <tr>
                                <td className="border border-gray-300 p-2 align-top">
                                    {awarenessStatements.map((statement, index) => (
                                        <div key={`awareness-${index}`}
                                            className="p-1 text-sm bg-pink-50 mb-2 border border-pink-200 rounded">
                                            {statement}
                                        </div>
                                    ))}
                                    {/* Fill remaining space if needed */}
                                    {awarenessStatements.length < 3 && (
                                        <div className="h-8"></div>
                                    )}
                                </td>
                                <td className="border border-gray-300 p-2 align-top">
                                    {sensitivityStatements.map((statement, index) => (
                                        <div key={`sensitivity-${index}`}
                                            className="p-1 text-sm bg-yellow-50 mb-2 border border-yellow-200 rounded">
                                            {statement}
                                        </div>
                                    ))}
                                    {/* Fill remaining space if needed */}
                                    {sensitivityStatements.length < 3 && (
                                        <div className="h-8"></div>
                                    )}
                                </td>
                                <td className="border border-gray-300 p-2 align-top">
                                    {creativityStatements.map((statement, index) => (
                                        <div key={`creativity-${index}`}
                                            className="p-1 text-sm bg-blue-50 mb-2 border border-blue-200 rounded">
                                            {statement}
                                        </div>
                                    ))}
                                    {/* Fill remaining space if needed */}
                                    {creativityStatements.length < 3 && (
                                        <div className="h-8"></div>
                                    )}
                                </td>
                            </tr>

                            {/* Teacher Use Row */}
                            <tr>
                                <td colSpan="3" className="border border-gray-300 p-1 text-xs italic text-center">(For Teacher's Use Only)</td>
                            </tr>
                            <tr>
                                <td className="border border-gray-300 p-2 text-sm">
                                    <div>No. of statements circled for Awareness: {awarenessCount}</div>
                                </td>
                                <td className="border border-gray-300 p-2 text-sm">
                                    <div>No. of statements circled for Sensitivity: {sensitivityCount}</div>
                                </td>
                                <td className="border border-gray-300 p-2 text-sm">
                                    <div>No. of statements circled for Creativity: {creativityCount}</div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Needs section */}
                <div className="mb-1 p-2">
                    <div className="flex">
                        <div>My peer needs to practice</div>
                        <div className="border-b border-gray-400 px-2 mx-2 flex-1">
                            {learnings.needs_practice || "Don't know"}
                        </div>
                        <div>. My peer needs help with</div>
                        <div className="border-b border-gray-400 px-2 mx-2 flex-1">
                            {learnings.needs_help || "Don't know"}
                        </div>
                        <div>.</div>
                    </div>
                </div>

            </div>

            <table className="w-full mt-1 border border-orange-400 text-sm bg-white">
                <tbody>
                    {/* Header Row */}
                    <tr>
                        <td colSpan={2} className="bg-[#f89646] text-white text-center font-bold p-2 border-b border-orange-400">
                            How to develop a Holistic Progress Summary? (for teacher's use only)
                        </td>
                    </tr>

                    {/* First Full-Width Instruction */}
                    <tr>
                        <td colSpan={2} className="p-2 bg-[#fde9d9] text-justify align-top">
                            Before filling out the Teacher's Feedback Form, kindly calculate the score for the 3 abilities
                            being assessed by the Student's Self Reflection sheet and the Peer Feedback sheet. For each
                            ability i.e. A (Awareness), S (Sensitivity) and C (Creativity), six sub-factors are being
                            assessed
                            (refer appendix).
                        </td>
                    </tr>

                    {/* Second Row: Paragraph Left, Scoring Key Right */}
                    <tr>
                        <td className="p-2 bg-[#fde9d9] text-justify align-top w-[70%]">
                            Calculate the number of statements being circled by the student in the
                            self-reflection sheet and by the peer in the peer feedback form. Use the scoring key provided
                            here to assess the
                            student's level of performance on each ability. Indicate the level of performance as reflected
                            on the student and peer assessment sheet in the Student Progress Wheel on the teacher's feedback
                            form.
                        </td>
                        <td className="p-2 bg-[#fde9d9] align-top w-[30%]">
                            <div className="border border-gray-400 p-2 bg-[#fde9d9] min-w-[170px]">
                                <div className="font-bold mb-1 text-center">Scoring Key:</div>
                                <div className="flex flex-col gap-1 items-start">
                                    <div><span className="font-bold">Beginner</span> - 0, 1, 2</div>
                                    <div><span className="font-bold">Proficient</span> - 3, 4</div>
                                    <div><span className="font-bold">Advanced</span> - 5, 6</div>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>


            {/* Page number removed as requested */}
        </div>
    );
};

export default PeerFeedbackPage;